import { Doughnut } from "react-chartjs-2";

import { useMemo } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  BarElement,
  LinearScale,
  PointElement,
  LineElement,
  Title,
} from "chart.js";

import LineProgress from "./LineProgress";
import type { DailyProgress } from "@/types/extended-types";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type DailyChallengeProps = {
  donePercentage: number;
  notDonePercentage: number;
  dailyData?: DailyProgress;
};

const DailyChallenge = ({
  donePercentage,
  notDonePercentage,
  dailyData,
}: DailyChallengeProps) => {
  const scorePointsProgress = dailyData?.scoreProgress || 0;
  const scorePointsTotal = dailyData?.scoreTarget || 30;
  const quizProgress = dailyData?.quizProgress || 0;
  const quizTotal = dailyData?.quizTarget || 1;

  const dognutData = useMemo(
    () => ({
      labels: ["Done", "Pending"],
      datasets: [
        {
          label: "Percentage",
          data: [donePercentage, notDonePercentage],
          backgroundColor: [
            "rgba(255, 255, 255, 1)",
            "rgba(255, 255, 255, 0.15)",
          ],
          borderColor: ["rgba(255, 255, 255, 1)", "rgba(255, 255, 255, 0.15)"],
          borderWidth: 1,
          borderRadius: 20,
        },
      ],
    }),
    []
  );

  const textInsideDognut = {
    id: "textInsideDognut",
    beforeDatasetsDraw(chart: any) {
      const { ctx } = chart;
      const xCenter = chart.getDatasetMeta(0).data[0].x;
      const yCenter = chart.getDatasetMeta(0).data[0].y;

      ctx.save();
      ctx.font = "bold 15px sans-serif";
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      const text = donePercentage + "% Done";
      ctx.fillText(text, xCenter, yCenter);
    },
  };

  const dognutOptions = {
    cutout: 90,
    plugins: {
      legend: {
        display: false,
      },
      datalabels: { display: false },
    },
    rotation: 180,
  };

  return (
    <article className="daily-challenge-container lg:max-w-1/2">
      <h3 className="mb-3">Daily Challenge</h3>
      <div className="flex flex-col lg:flex-row items-center">
        <div className="mb-4 lg:mb-0 max-w-1/2 w-1/2">
          <Doughnut
            data={dognutData}
            width={200}
            height={200}
            options={dognutOptions}
            plugins={[textInsideDognut]}
          />
        </div>
        <div className="w-full lg:w-1/2 lg:max-w-1/2 ml-5 flex flex-col justify-between">
          <div className="mb-5">
            <div className="flex items-center justify-between">
              <span className="text-s">Score {scorePointsTotal} points</span>
              <span className="text-sm">
                {scorePointsProgress} / {scorePointsTotal}
              </span>
            </div>
            <LineProgress
              progress={scorePointsProgress}
              total={scorePointsTotal}
              bgColor={"#fff"}
              backdropColor={"rgba(255, 255, 255, 0.5)"}
            />
          </div>
          <div className="mb-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-s">Take a quiz</span>
              <span className="text-sm">
                {quizProgress} / {quizTotal}
              </span>
            </div>
            <LineProgress
              progress={quizProgress}
              total={quizTotal}
              bgColor={"#fff"}
              backdropColor={"rgba(255, 255, 255, 0.5)"}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

export default DailyChallenge;
